import { useState, useEffect, useCallback } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import StatusBadge from "@/components/StatusBadge";
import api, { formatApiErrorDetail } from "@/lib/api";
import JobAttachments from "@/components/JobAttachments";
import ChatDialog from "@/components/ChatDialog";
import { Briefcase, PlusCircle, Users, Send, UserCog, FileText, Inbox, Hammer } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { startCheckout } from "@/lib/payments";
import { toast } from "sonner";
import { Empty } from "@/components/bedrijf/Empty";
import { ProBanners } from "@/components/bedrijf/ProBanners";
import { AdSlot } from "@/components/AdSlot";
import { JobCard } from "@/components/bedrijf/JobCard";
import { JobForm } from "@/components/bedrijf/JobForm";
import { MonteurFilters } from "@/components/bedrijf/MonteurFilters";
import { MonteurCard } from "@/components/bedrijf/MonteurCard";
import { PortfolioManager } from "@/components/PortfolioManager";
import { ProfileForm } from "@/components/bedrijf/ProfileForm";
import { ReageerOpdrachten } from "@/components/bedrijf/ReageerOpdrachten";
import { HireDialog } from "@/components/bedrijf/HireDialog";
import { AgendaDialog } from "@/components/bedrijf/AgendaDialog";
import { InterestsDialog } from "@/components/bedrijf/InterestsDialog";
import { OfferteForm } from "@/components/offerte/OfferteForm";
import { MijnOffertes } from "@/components/offerte/MijnOffertes";
import { OntvangenOffertes } from "@/components/offerte/OntvangenOffertes";

const BEDRIJF_TABS = [
  { key: "opdrachten", label: "Mijn opdrachten", icon: Briefcase },
  { key: "plaatsen", label: "Plaats opdracht", icon: PlusCircle },
  { key: "monteurs", label: "Vind professionals", icon: Users },
  { key: "aanvragen", label: "Verzonden aanvragen", icon: Send },
  { key: "offerteaanvragen", label: "Offerteaanvragen", icon: Inbox },
  { key: "profiel", label: "Uw profiel", icon: UserCog },
];

const PARTICULIER_TABS = [
  { key: "offertes", label: "Mijn offertes", icon: FileText },
  { key: "offerte-nieuw", label: "Offerte aanvragen", icon: PlusCircle },
  { key: "monteurs", label: "Vind professionals", icon: Users },
  { key: "aanvragen", label: "Verzonden aanvragen", icon: Send },
  { key: "profiel", label: "Uw profiel", icon: UserCog },
];

export default function BedrijfDashboard() {
  const { user, refreshUser } = useAuth();
  const isParticulier = user?.role === "particulier";
  const baseTabs = isParticulier ? PARTICULIER_TABS : BEDRIJF_TABS;
  const TABS = (!isParticulier && user?.plaatst_voor_collega)
    ? [...baseTabs.slice(0, 1), { key: "reageren", label: "Reageren op opdrachten", icon: Hammer }, ...baseTabs.slice(1)]
    : baseTabs;
  const roleLabel = isParticulier ? "Particulier" : "Bedrijf";
  const [verifying, setVerifying] = useState(false);
  const [profile, setProfile] = useState(user || {});
  const [savingProfile, setSavingProfile] = useState(false);
  const [tab, setTab] = useState(isParticulier ? "offertes" : "opdrachten");
  const [jobs, setJobs] = useState([]);
  const [monteurs, setMonteurs] = useState([]);
  const [requests, setRequests] = useState([]);
  const [offertes, setOffertes] = useState([]);
  const [ontvangenOffertes, setOntvangenOffertes] = useState([]);
  const [submittingOfferte, setSubmittingOfferte] = useState(false);
  const [filters, setFilters] = useState({ specialisatie: "alle", regio: "", provincie: "alle", alleen_beschikbaar: false, werkstatuut: "alle", vervoer: "alle", rijbewijs: "alle", taal: "alle", leiding_team: false, eigen_materiaal: false, beschikbaar_op: "" });
  const [job, setJob] = useState({ titel: "", beschrijving: "", locatie: "", provincie: "", types: [], type_andere: "", vergoedingstype: "uurtarief", start_datum: "", bijlagen: [] });
  const [uploading, setUploading] = useState(false);
  const [hireFor, setHireFor] = useState(null);
  const [hire, setHire] = useState({ titel: "", bericht: "", bijlagen: [] });
  const [editingId, setEditingId] = useState(null);
  const [chatJob, setChatJob] = useState(null);
  const [chatMonteurId, setChatMonteurId] = useState(null);
  const [interestsFor, setInterestsFor] = useState(null);
  const [interestsList, setInterestsList] = useState([]);
  const [agendaFor, setAgendaFor] = useState(null);
  const [agendaData, setAgendaData] = useState({});

  const load = useCallback(async () => {
    try {
      const [j, r] = await Promise.all([api.get("/jobs"), api.get("/hire-requests")]);
      setJobs(j.data.jobs);
      setRequests(r.data.requests);
    } catch (e) { toast.error("Laden mislukt"); }
  }, []);

  const loadMonteurs = useCallback(async () => {
    try {
      const params = {};
      if (filters.specialisatie !== "alle") params.specialisatie = filters.specialisatie;
      if (filters.regio) params.regio = filters.regio;
      if (filters.provincie !== "alle") params.provincie = filters.provincie;
      if (filters.alleen_beschikbaar) params.alleen_beschikbaar = true;
      if (filters.werkstatuut !== "alle") params.werkstatuut = filters.werkstatuut;
      if (filters.vervoer !== "alle") params.vervoer = filters.vervoer;
      if (filters.rijbewijs !== "alle") params.rijbewijs = filters.rijbewijs;
      if (filters.taal !== "alle") params.taal = filters.taal;
      if (filters.leiding_team) params.leiding_team = true;
      if (filters.eigen_materiaal) params.eigen_materiaal = true;
      if (filters.beschikbaar_op) params.beschikbaar_op = filters.beschikbaar_op;
      const { data } = await api.get("/monteurs", { params });
      setMonteurs(data.monteurs);
    } catch (e) { toast.error("Laden mislukt"); }
  }, [filters]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (user) setProfile(user); }, [user]);
  useEffect(() => { if (tab === "monteurs") loadMonteurs(); }, [tab, loadMonteurs]);

  const loadOffertes = useCallback(async () => {
    try { const { data } = await api.get("/offertes/mijn"); setOffertes(data.offertes); }
    catch (e) { console.error("Offertes laden mislukt:", e?.message || e); }
  }, []);

  const loadOntvangenOffertes = useCallback(async () => {
    try { const { data } = await api.get("/offertes/ontvangen"); setOntvangenOffertes(data.offertes); }
    catch (e) { console.error("Ontvangen offertes laden mislukt:", e?.message || e); }
  }, []);

  useEffect(() => { if (isParticulier) loadOffertes(); }, [isParticulier, loadOffertes]);
  useEffect(() => { if (!isParticulier && tab === "offerteaanvragen") loadOntvangenOffertes(); }, [isParticulier, tab, loadOntvangenOffertes]);

  const submitOfferte = async (payload) => {
    setSubmittingOfferte(true);
    try {
      const { data } = await api.post("/offertes", payload);
      await loadOffertes();
      if (data.payment_required) {
        toast.success("Offerteaanvraag aangemaakt — rond de €3-betaling af om te versturen.");
        await startCheckout({ kind: "offerte", offerteId: data.offerte.id });
      } else {
        toast.success("Offerteaanvraag gratis verstuurd — de gekozen bedrijven zijn verwittigd.");
        setTab("offertes");
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSubmittingOfferte(false);
    }
  };

  const deleteOfferte = async (id) => {
    try {
      await api.delete(`/offertes/${id}`);
      toast.success("Offerteaanvraag verwijderd");
      loadOffertes();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const sendOfferteFree = async (id) => {
    try {
      await api.post(`/offertes/${id}/verstuur-gratis`);
      toast.success("Offerteaanvraag gratis verstuurd — de gekozen bedrijven zijn verwittigd.");
      loadOffertes();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const extendOfferte = async (id) => {
    try { await api.post(`/offertes/${id}/verleng`); toast.success("Offerteaanvraag verlengd met 1 maand."); loadOffertes(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Verlengen mislukt"); }
  };

  const setOfferteStatus = async (id, status) => {
    try {
      await api.post(`/offertes/${id}/bedrijf-status`, { status });
      toast.success(status === "offerte_verstuurd" ? "Gemarkeerd als offerte verstuurd" : "Gemarkeerd als niet geïnteresseerd");
      loadOntvangenOffertes();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  // Ververs opdrachten/aanvragen bij openen tab + periodiek (om toegewezen monteur & chat te zien)
  useEffect(() => {
    if (tab !== "opdrachten" && tab !== "aanvragen") return;
    load();
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, [tab, load]);

  const openInterests = async (j) => {
    setInterestsFor(j);
    setInterestsList([]);
    try {
      const { data } = await api.get(`/jobs/${j.id}/interesses`);
      setInterestsList(data.interesses);
    } catch (e) { toast.error("Laden mislukt"); }
  };

  const chooseMonteur = async (monteurId) => {
    try {
      await api.post(`/jobs/${interestsFor.id}/kies`, { monteur_id: monteurId });
      toast.success("Professional gekozen! De opdracht is toegewezen.");
      setInterestsFor(null);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const openChat = (job, monteurId) => { setChatJob(job); setChatMonteurId(monteurId); };

  const openAgenda = async (m) => {
    setAgendaFor(m);
    setAgendaData({});
    try {
      const { data } = await api.get(`/agenda/${m.id}`);
      setAgendaData(data.agenda);
    } catch (e) { toast.error("Agenda laden mislukt"); }
  };

  const createJob = async (e) => {
    e.preventDefault();
    if (job.types.length === 0 && !job.type_andere) {
      toast.error("Kies minstens één type werk");
      return;
    }
    const payload = {
      titel: job.titel, beschrijving: job.beschrijving, locatie: job.locatie,
      provincie: job.provincie,
      types: job.types, type_andere: job.type_andere,
      vergoedingstype: job.vergoedingstype,
      start_datum: job.start_datum, duur: "",
      bijlagen: job.bijlagen,
    };
    try {
      if (editingId) {
        await api.put(`/jobs/${editingId}`, payload);
        toast.success("Opdracht bijgewerkt!");
        resetJobForm();
        load();
        setTab("opdrachten");
      } else {
        const { data } = await api.post("/jobs", payload);
        resetJobForm();
        if (user?.role === "particulier") {
          toast.success("Opdracht aangemaakt — rond de betaling af om live te gaan.");
          await startCheckout({ kind: "job", jobId: data.job.id });
          return;
        }
        toast.success("Opdracht geplaatst!");
        load();
        setTab("opdrachten");
      }
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const resetJobForm = () => {
    setEditingId(null);
    setJob({ titel: "", beschrijving: "", locatie: "", provincie: user?.provincie || "", types: [], type_andere: "", vergoedingstype: "uurtarief", start_datum: "", bijlagen: [] });
  };

  const editJob = (j) => {
    setEditingId(j.id);
    setJob({
      titel: j.titel || "", beschrijving: j.beschrijving || "", locatie: j.locatie || "",
      provincie: j.provincie || user?.provincie || "",
      types: j.types || [], type_andere: j.type_andere || "",
      vergoedingstype: j.vergoedingstype || "uurtarief",
      start_datum: j.start_datum || "", bijlagen: j.bijlagen || [],
    });
    setTab("plaatsen");
  };

  const uploadFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // sta toe hetzelfde bestand opnieuw te kiezen
    if (!file) return;
    const allowed = ["application/pdf", "image/png", "image/gif"];
    if (!allowed.includes(file.type)) {
      toast.error("Alleen PDF, PNG of GIF toegestaan");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/jobs/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setJob((j) => ({ ...j, bijlagen: [...j.bijlagen, data] }));
      toast.success(`${data.naam} toegevoegd`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload mislukt");
    } finally {
      setUploading(false);
    }
  };

  const removeBijlage = (id) => setJob((j) => ({ ...j, bijlagen: j.bijlagen.filter((b) => b.id !== id) }));

  const toggleJobType = (val) => {
    const arr = job.types || [];
    setJob({ ...job, types: arr.includes(val) ? arr.filter((x) => x !== val) : [...arr, val] });
  };

  const deleteJob = async (id) => {
    try { await api.delete(`/jobs/${id}`); toast.success("Opdracht verwijderd"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const extendJob = async (id) => {
    try { await api.post(`/jobs/${id}/verleng`); toast.success("Opdracht verlengd met 1 maand."); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Verlengen mislukt"); }
  };

  const openHire = (m) => { setHireFor(m); setHire({ titel: "", bericht: "", bijlagen: [] }); };

  const uploadHireFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const allowed = ["application/pdf", "image/png", "image/gif"];
    if (!allowed.includes(file.type)) {
      toast.error("Alleen PDF, PNG of GIF toegestaan");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/jobs/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setHire((h) => ({ ...h, bijlagen: [...h.bijlagen, data] }));
      toast.success(`${data.naam} toegevoegd`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload mislukt");
    } finally {
      setUploading(false);
    }
  };

  const removeHireBijlage = (id) => setHire((h) => ({ ...h, bijlagen: h.bijlagen.filter((b) => b.id !== id) }));

  const sendHire = async () => {
    try {
      await api.post("/hire-requests", { monteur_id: hireFor.id, titel: hire.titel, bericht: hire.bericht, bijlagen: hire.bijlagen });
      toast.success(`Aanvraag verstuurd naar ${hireFor.name}`);
      setHireFor(null);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const saveProfile = async () => {
    setSavingProfile(true);
    try {
      const payload = { name: profile.name, telefoon: profile.telefoon, regio: profile.regio, provincie: profile.provincie };
      if (user?.role === "bedrijf") {
        payload.bedrijfsnaam = profile.bedrijfsnaam;
        payload.btw_nummer = profile.btw_nummer;
        payload.website = profile.website || "";
        payload.ik_bied_aan = profile.ik_bied_aan || [];
        payload.aanbod_voor = profile.aanbod_voor || [];
        payload.aanbod_andere = profile.aanbod_andere || "";
        payload.materialen = profile.materialen || [];
        payload.merken_pvc = profile.merken_pvc || [];
        payload.merken_pvc_andere = profile.merken_pvc_andere || "";
        payload.merken_alu = profile.merken_alu || [];
        payload.merken_alu_andere = profile.merken_alu_andere || "";
        payload.merken_hout = profile.merken_hout || [];
        payload.merken_hout_andere = profile.merken_hout_andere || "";
        payload.plaatst_voor_collega = !!profile.plaatst_voor_collega;
      }
      if (user?.role === "particulier") {
        payload.adres = profile.adres;
        payload.op_zoek_naar = profile.op_zoek_naar || [];
      }
      const { data } = await api.put("/opdrachtgever/me", payload);
      refreshUser(data.user);
      setProfile(data.user);
      toast.success("Profiel opgeslagen");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSavingProfile(false);
    }
  };

  const verifyBtw = async () => {
    setVerifying(true);
    try {
      const { data } = await api.post("/bedrijf/verifieer-btw");
      refreshUser(data.user);
      if (data.resultaat?.geldig) toast.success("BTW-nummer geverifieerd!");
      else toast.error(data.resultaat?.boodschap || "Verificatie mislukt.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setVerifying(false);
    }
  };

  const sub = { opdrachten: "Geplaatste opdrachten", plaatsen: "Nieuwe opdracht", monteurs: "Professionalsgids", aanvragen: "Directe aanvragen", offerteaanvragen: "Ontvangen offerteaanvragen", offertes: "Mijn offerteaanvragen", "offerte-nieuw": "Nieuwe offerteaanvraag", profiel: "Account & gegevens", reageren: "Reageren op opdrachten" }[tab] || "";
  const title = { opdrachten: "Mijn opdrachten", plaatsen: editingId ? "Opdracht aanpassen" : "Plaats opdracht", monteurs: "Vind professionals", aanvragen: "Verzonden aanvragen", offerteaanvragen: "Offerteaanvragen", offertes: "Mijn offertes", "offerte-nieuw": "Offerte aanvragen", profiel: "Uw profiel", reageren: "Reageren op opdrachten" }[tab] || "Work4you";
  const proPrice = user?.pro_intro_eligible ? "1e kwartaal €1" : "€19/kwartaal";

  return (
    <DashboardLayout title={title} subtitle={`${roleLabel} · ${sub}`} tabs={TABS} active={tab} onTab={setTab}>
      <AdSlot slot="dashboard_top" rol={user?.role} provincie={user?.provincie} className="mb-5 max-w-3xl" />
      <ProBanners user={user} verifying={verifying} verifyBtw={verifyBtw} proPrice={proPrice} />

      {tab === "reageren" && <ReageerOpdrachten />}

      {tab === "opdrachten" && (
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
          {jobs.length === 0 && <Empty text="Je hebt nog geen opdrachten geplaatst." />}
          {jobs.map((j) => (
            <JobCard key={j.id} job={j} onView={openInterests} onEdit={editJob} onDelete={deleteJob} onExtend={extendJob} />
          ))}
        </div>
      )}

      {tab === "offertes" && (
        <MijnOffertes offertes={offertes} onDelete={deleteOfferte} onSendFree={sendOfferteFree} onExtend={extendOfferte} />
      )}

      {tab === "offerte-nieuw" && (
        <OfferteForm user={user} onSubmit={submitOfferte} submitting={submittingOfferte} />
      )}

      {tab === "offerteaanvragen" && (
        <OntvangenOffertes offertes={ontvangenOffertes} userId={user?.id} onSetStatus={setOfferteStatus} />
      )}

      {tab === "plaatsen" && (
        <JobForm job={job} setJob={setJob} editingId={editingId} createJob={createJob}
          resetJobForm={resetJobForm} setTab={setTab} uploading={uploading}
          uploadFile={uploadFile} removeBijlage={removeBijlage} toggleJobType={toggleJobType} />
      )}

      {tab === "monteurs" && (
        <>
          <MonteurFilters filters={filters} setFilters={setFilters} />
          <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
            {monteurs.length === 0 && <Empty text="Geen professionals gevonden met deze filters." />}
            {monteurs.map((m) => (
              <MonteurCard key={m.id} monteur={m} onHire={openHire} onAgenda={openAgenda} />
            ))}
          </div>
        </>
      )}

      {tab === "aanvragen" && (
        <div className="grid md:grid-cols-2 gap-4">
          {requests.length === 0 && <Empty text="Je hebt nog geen aanvragen verstuurd." />}
          {requests.map((r) => (
            <div key={r.id} data-testid="sent-request-card" className="bg-white border border-slate-200 rounded-md p-5">
              <div className="flex items-start justify-between mb-2">
                <h3 className="font-display text-lg font-bold text-slate-950">{r.titel}</h3>
                <StatusBadge status={r.status} />
              </div>
              <p className="text-sm font-semibold text-slate-700 mb-2">Naar: {r.monteur_name}</p>
              <p className="text-sm text-slate-600 mb-3">{r.bericht}</p>
              <JobAttachments bijlagen={r.bijlagen} />
            </div>
          ))}
        </div>
      )}

      {tab === "profiel" && (
        <div className="space-y-6">
          <ProfileForm user={user} profile={profile} setProfile={setProfile}
            saveProfile={saveProfile} savingProfile={savingProfile} />
          {!isParticulier && (
            <div className="max-w-2xl">
              <PortfolioManager />
            </div>
          )}
        </div>
      )}

      <HireDialog hireFor={hireFor} setHireFor={setHireFor} hire={hire} setHire={setHire}
        sendHire={sendHire} uploading={uploading} uploadHireFile={uploadHireFile} removeHireBijlage={removeHireBijlage} />

      <AgendaDialog agendaFor={agendaFor} setAgendaFor={setAgendaFor} agendaData={agendaData} />

      <InterestsDialog interestsFor={interestsFor} setInterestsFor={setInterestsFor}
        interestsList={interestsList} onChat={openChat} onChoose={chooseMonteur} />

      <ChatDialog job={chatJob} monteurId={chatMonteurId} currentUserId={user?.id} open={!!chatJob} onClose={() => { setChatJob(null); setChatMonteurId(null); }} />
    </DashboardLayout>
  );
}
